// Orders.jsx - The list. Search, filter by status, and a table dense enough that
// a whole day's work fits on one screen.
//
// The URL carries the filter state so a link to a filtered view can be pasted
// into a message, which is how the dashboard links to its own queues.
//
// Every row carries its own actions. The thing that made this necessary: doing
// one obvious thing to an order, printing its labels or answering a text, used
// to mean opening the order, finding the tab and coming back. At a hundred
// orders a day that is three hundred page loads to do a hundred things, so the
// two or three actions that make sense for a given status are on the row.

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import clsx from "clsx";

import { useAsync, useDebounced } from "../lib/hooks.js";
import { ordersApi, affiliatesApi } from "../lib/api.js";
import { money, ago, phone } from "../lib/format.js";
import { Panel, Input, Field, Loading, Alert, Empty, Tag, Button, Modal, Select, Menu } from "../ui/kit.jsx";
import { useForm } from "../lib/hooks.js";
import { useToast } from "../ui/kit.jsx";
import { FormAddressField } from "../ui/address.jsx";
import { Loupe, Plus, Flag, ArrowRight, Note, Printer, Truck, Message, Check, Camera, Close } from "../ui/icons.jsx";

const FILTERS = [
  { key: "", label: "All open", query: { open: "true" } },
  { key: "vetting", label: "To review", query: { status: "new,vetting" } },
  { key: "approved", label: "Kit to send", query: { status: "approved" } },
  { key: "transit", label: "In transit", query: { status: "kit_sent,kit_delivered,in_transit" } },
  { key: "received", label: "In the building", query: { status: "received,evaluating" } },
  { key: "offer", label: "Offer out", query: { status: "offer_made" } },
  { key: "action", label: "Needs action", query: { status: "accepted,declined" } },
  { key: "closed", label: "Closed", query: { status: "paid,returned,cancelled" } },
];

// Separate from the status filters above, because it is a different question.
// "Which of these is an affiliate order" cuts across every stage.
const AFFILIATE_FILTER = { key: "affiliate", label: "Affiliate orders" };

const TONE = {
  new: "caution", vetting: "caution", received: "critical", accepted: "positive",
  declined: "caution", paid: "positive", cancelled: "neutral",
};

////////// AFFILIATE PICKER //////////
// Nobody remembers a code. They remember a name, and the customer on the phone
// said the name, so this searches on both and fills the code in itself. Choosing
// from the list is the normal path; typing a code straight in still works,
// because somebody reading one off a card should not have to wait for a menu.

function AffiliatePicker({ value, onChange }) {
  const [term, setTerm] = useState("");
  const [chosen, setChosen] = useState(null);
  const debounced = useDebounced(term, 200);

  const { data } = useAsync(
    (signal) => (debounced.trim() && !chosen ? affiliatesApi.search(debounced.trim(), signal) : Promise.resolve(null)),
    [debounced, chosen]
  );

  const pick = (match) => {
    setChosen(match);
    setTerm("");
    onChange(match.code);
  };

  if (chosen || value) {
    return (
      <Field label="Affiliate" hint="This order will be credited to them at the current commission.">
        <div className="flex items-center gap-2 px-3 py-2 border border-bronze bg-bronze-wash rounded-xs">
          <span className="tag tag-bronze num shrink-0">{chosen?.code || value}</span>
          <span className="text-[14px] text-ink truncate flex-1">{chosen?.name || "Code entered by hand"}</span>
          <button
            type="button"
            onClick={() => { setChosen(null); onChange(""); }}
            className="text-faint hover:text-critical shrink-0 p-1"
            aria-label="Remove the affiliate"
          >
            <Close size={15} />
          </button>
        </div>
      </Field>
    );
  }

  return (
    <Field label="Affiliate" hint="Optional. Type a name or a code if somebody sent this customer.">
      <div className="relative">
        <input
          className="field"
          placeholder="Dave, or 4K7M2"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          autoComplete="off"
          aria-label="Search affiliates"
        />

        {data?.matches?.length > 0 && (
          <ul className="absolute z-20 left-0 right-0 mt-1 bg-card border border-rule shadow-lift max-h-56 overflow-y-auto rounded-xs">
            {data.matches.map((match) => (
              <li key={match.code}>
                <button
                  type="button"
                  onClick={() => pick(match)}
                  className="w-full text-left px-3 py-2 hover:bg-surface flex items-center gap-2"
                >
                  <span className="tag tag-bronze num shrink-0">{match.code}</span>
                  <span className="text-[13.5px] text-ink truncate">{match.name}</span>
                  {match.company && <span className="text-[12px] text-faint truncate">{match.company}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Nothing found, but they typed something that looks like a code. Take
            it: the server checks it and says so if it is wrong. */}
        {debounced.trim().length >= 3 && data?.matches?.length === 0 && (
          <button
            type="button"
            onClick={() => pick({ code: debounced.trim().toUpperCase(), name: "Code entered by hand" })}
            className="absolute z-20 left-0 right-0 mt-1 bg-card border border-rule shadow-lift px-3 py-2 text-left text-[13px] text-muted hover:bg-surface rounded-xs"
          >
            No affiliate matched. Use <span className="num text-ink">{debounced.trim().toUpperCase()}</span> as a code anyway.
          </button>
        )}
      </div>
    </Field>
  );
}

////////// NEW ORDER //////////
// For orders taken over the phone or at a show.

function NewOrderModal({ open, onClose, onCreated }) {
  const toast = useToast();
  const form = useForm({
    firstName: "", lastName: "", email: "", phone: "",
    address: { line1: "", city: "", state: "", postal: "", country: "US" },
    source: "phone", status: "approved", smsConsent: false, internalNotes: "",
    affiliateCode: "",
  });

  const submit = form.submit(async (values) => {
    const order = await ordersApi.create(values);
    toast.success(`Created ${order.number}`);
    onCreated(order);
    return order;
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New order"
      description="For a customer who called, or someone you met at a show."
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={form.busy}>Create order</Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="First name" required {...form.field("firstName")} />
          <Input label="Last name" required {...form.field("lastName")} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Email" type="email" {...form.field("email")} />
          <Input label="Phone" type="tel" {...form.field("phone")} />
        </div>
        <FormAddressField form={form} label="Street address" hint="Pick from the list to fill the rest" />
        <div className="grid grid-cols-3 gap-4">
          <Input label="City" {...form.field("address.city")} />
          <Input label="State" maxLength={2} {...form.field("address.state")} />
          <Input label="ZIP" {...form.field("address.postal")} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Select
            label="Where from"
            options={[
              { value: "phone", label: "Phone call" },
              { value: "show", label: "Show" },
              { value: "referral", label: "Referral" },
              { value: "import", label: "Import" },
            ]}
            {...form.field("source")}
          />
          <Select
            label="Starting status"
            options={[
              { value: "approved", label: "Approved, kit to send" },
              { value: "received", label: "Items already in hand" },
              { value: "vetting", label: "Needs vetting" },
            ]}
            {...form.field("status")}
          />
        </div>
        <AffiliatePicker
          value={form.values.affiliateCode}
          onChange={(code) => form.set("affiliateCode", code)}
        />

        {form.message && <Alert tone="error">{form.message.text}</Alert>}
      </form>
    </Modal>
  );
}

////////// ROW ACTIONS //////////
// What is worth doing to an order without opening it. Kept to the few that are
// obvious from a row: everything else is a decision, and a decision needs the
// order in front of you.
//
// Only ever the actions that apply to the status in that row, because a menu of
// eight things where six are wrong is slower to read than opening the order.

function RowActions({ row, onChanged }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const run = async (job, message) => {
    setBusy(true);
    try {
      await job();
      toast.success(message);
      onChanged();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const print = (numbers) => window.open(`/admin/kits/print?numbers=${numbers.join(",")}`, "_blank", "noopener");

  const items = [
    { label: "Open the order", to: `/admin/orders/${row.number}`, icon: ArrowRight },
    { label: "Message them", to: `/admin/orders/${row.number}?tab=messages`, icon: Message, hint: row.phone ? "Text and email" : "Email only" },

    ["new", "vetting"].includes(row.status) && {
      label: "Approve and tell them",
      icon: Check,
      disabled: busy,
      onSelect: () => run(
        () => ordersApi.setStatus(row.number, { status: "approved", notify: true }),
        `${row.number} approved`
      ),
    },

    // Buying and printing are two entries rather than one, and deliberately so:
    // a tab opened after an await is a tab a popup blocker eats. Buying refreshes
    // the row, and the print action is then the one waiting in the same menu.
    row.status === "approved" && !row.kit?.bought && {
      label: "Buy the kit labels",
      icon: Truck,
      disabled: busy || !row.city,
      hint: row.city ? "Then print them from here" : "No address on this order",
      onSelect: () => run(async () => {
        const { results } = await ordersApi.buyKits([row.number]);
        if (!results[0]?.ok) throw new Error(results[0]?.reason || "The labels could not be bought");
      }, "Bought. Print them from the same menu"),
    },

    row.kit?.bought && {
      label: "Print the labels",
      icon: Printer,
      hint: "Return label, then the outside one",
      onSelect: () => print([row.number]),
    },

    ["kit_delivered", "in_transit"].includes(row.status) && {
      label: "Mark it received",
      icon: Camera,
      disabled: busy,
      hint: "Then film the opening",
      onSelect: () => run(
        () => ordersApi.setStatus(row.number, { status: "received", notify: true }),
        `${row.number} marked received`
      ),
    },
  ];

  return <Menu label="Actions" items={items} />;
}

////////// LIST //////////

export default function Orders() {
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get("q") || "");
  const [creating, setCreating] = useState(false);
  const debounced = useDebounced(search, 300);

  const status = params.get("status") || "";
  const affiliateOnly = params.get("affiliate") === "any";
  const filterKey = params.get("filter") ?? (status ? "" : "");
  const page = Number(params.get("page")) || 1;

  useEffect(() => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (debounced) next.set("q", debounced); else next.delete("q");
      if (debounced && next.get("page")) next.delete("page");
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const query = {
    q: debounced || undefined,
    affiliate: affiliateOnly ? "any" : undefined,
    page,
    limit: 50,
    ...(status ? { status } : filterKey === "" && !debounced ? { open: "true" } : {}),
  };

  const { data, loading, error, reload } = useAsync(
    (signal) => ordersApi.list(query, signal),
    [debounced, status, affiliateOnly, page]
  );

  const applyFilter = (filter) => {
    const next = new URLSearchParams();
    if (debounced) next.set("q", debounced);
    if (filter.query.status) next.set("status", filter.query.status);
    if (filter.key) next.set("filter", filter.key);
    // Kept across a status change, because somebody looking at affiliate orders
    // is still looking at affiliate orders after they pick a stage.
    if (affiliateOnly) next.set("affiliate", "any");
    setParams(next);
  };

  const toggleAffiliates = () => {
    const next = new URLSearchParams(params);
    if (affiliateOnly) next.delete("affiliate");
    else next.set("affiliate", "any");
    next.delete("page");
    setParams(next);
  };

  const activeKey = FILTERS.find((filter) => filter.query.status === status)?.key ?? "";

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px]">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="display display-lg text-ink">Orders</h1>
          {data && <p className="hint mt-1 num">{data.total} matching</p>}
        </div>
        <Button variant="quiet" onClick={() => setCreating(true)} className="w-full xs:w-auto">
          <Plus size={16} /> New order
        </Button>
      </header>

      <div className="flex flex-col lg:flex-row gap-3 mb-5">
        <div className="lg:w-80 shrink-0">
          <Input
            placeholder="Name, email, phone, order or tracking number"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            prefix={<Loupe size={16} />}
            className="pl-9"
            aria-label="Search orders"
          />
        </div>

        <div className="chip-row">
          {FILTERS.map((filter) => (
            <button
              key={filter.label}
              type="button"
              onClick={() => applyFilter(filter)}
              className={clsx("btn btn-sm", activeKey === filter.key && (filter.key || !status) ? "btn-ink" : "btn-quiet")}
            >
              {filter.label}
            </button>
          ))}

          <span className="w-px bg-rule self-stretch mx-1 shrink-0" aria-hidden="true" />

          <button
            type="button"
            onClick={toggleAffiliates}
            className={clsx("btn btn-sm", affiliateOnly ? "btn-ink" : "btn-quiet")}
            aria-pressed={affiliateOnly}
          >
            {AFFILIATE_FILTER.label}
          </button>
        </div>
      </div>

      {error && <Alert tone="error" className="mb-4">{error.message}</Alert>}

      {/* Under 640px the table is a stack of cards, each with its own border, so
          the panel's own border and background would frame a frame. */}
      <Panel className="overflow-hidden max-sm:border-0 max-sm:bg-transparent">
        {loading ? (
          <Loading />
        ) : !data?.items?.length ? (
          <Empty
            title={debounced ? "Nothing matched that" : "No orders here"}
            icon={Note}
            action={debounced ? <Button variant="quiet" onClick={() => setSearch("")}>Clear the search</Button> : null}
          >
            {debounced ? `Nothing matches "${debounced}".` : "Change the filter, or create one by hand."}
          </Empty>
        ) : (
          <div className="sm:overflow-x-auto">
            <table className="grid-table stack-table w-full sm:min-w-[900px]">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Where</th>
                  <th>Status</th>
                  <th>Tracking</th>
                  <th className="text-right">Offer</th>
                  <th className="text-right">Updated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.items.map((order) => (
                  <tr key={order.id} className="group">
                    <td className="num" data-label="Order">
                      <Link to={`/admin/orders/${order.number}`} className="font-semibold text-ink hover:text-bronze-dark">
                        {order.number}
                      </Link>
                      {order.vettingFlags?.length > 0 && (
                        <Flag size={13} className="inline ml-1.5 -mt-0.5 text-caution" aria-label="Flagged in vetting" />
                      )}
                    </td>
                    <td data-label="Customer">
                      <span className="block text-ink">
                        {order.name || "—"}
                        {order.affiliate && (
                          <span className="tag tag-bronze ml-2 align-middle" title={`Referred by ${order.affiliate.name}`}>
                            {order.affiliate.code}
                          </span>
                        )}
                      </span>
                      <span className="block text-[12px] text-faint num">{order.phone ? phone(order.phone) : order.email}</span>
                    </td>
                    <td className="text-muted whitespace-nowrap stack-hide" data-label="Where">
                      {order.city ? `${order.city}, ${order.state}` : "—"}
                    </td>
                    <td data-label="Status">
                      <Tag tone={TONE[order.status] || "neutral"}>{order.statusLabel}</Tag>
                    </td>
                    <td className="num text-[12.5px] text-muted stack-hide" data-label="Tracking">{order.tracking || "—"}</td>
                    <td className="text-right num font-semibold text-ink" data-label="Offer">
                      {order.offer ? money(order.offer) : "—"}
                    </td>
                    <td className="text-right text-[12.5px] text-faint whitespace-nowrap" data-label="Updated">{ago(order.updatedAt)}</td>
                    <td className="text-right" data-label="">
                      <RowActions row={order} onChanged={reload} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {data && data.pages > 1 && (
        <div className="flex items-center justify-between gap-4 mt-4">
          <p className="hint num">Page {data.page} of {data.pages}</p>
          <div className="flex gap-2">
            <Button
              variant="quiet"
              size="sm"
              disabled={page <= 1}
              onClick={() => setParams((previous) => {
                const next = new URLSearchParams(previous);
                next.set("page", String(page - 1));
                return next;
              })}
            >
              Previous
            </Button>
            <Button
              variant="quiet"
              size="sm"
              disabled={page >= data.pages}
              onClick={() => setParams((previous) => {
                const next = new URLSearchParams(previous);
                next.set("page", String(page + 1));
                return next;
              })}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <NewOrderModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => { setCreating(false); reload(); }}
      />
    </div>
  );
}
